/*
 * "Is this person signed in?" — for a page that must not guess wrong.
 *
 * ── Why not read localStorage ──────────────────────────────────────────────
 *
 * Because everything else in this app does, and it is wrong here. Roughly
 * forty components decide with a synchronous
 * `localStorage.getItem("access_token") !== null`, the header included
 * (Header_test/index.tsx:60). That answer is available at first paint, which
 * is exactly why it is popular — and it is the answer from BEFORE the silent
 * session check has run.
 *
 * On a page with two designs on one route, being wrong for 400ms is not a
 * detail: a signed-in visitor reads the marketing hero and then watches it
 * swap to their name. The old home page never noticed because it looked
 * identical either way.
 *
 * ── What this uses instead ─────────────────────────────────────────────────
 *
 * The OIDC client's own view. `silentSignIn()` settles the question — from a
 * refresh token in this tab, or by a top-level round trip to the provider —
 * and `getStatus()` / `getUser()` report the result. Until it settles, the
 * honest answer is "unknown", and the page renders its frame with the
 * decision area blank rather than picking a side and correcting itself.
 *
 * ── The memo key, which is load-bearing ────────────────────────────────────
 *
 * `createAuth` is memoised on `issuer|clientId|redirectUri`
 * (zodiacAuth.js:231-250). Matching all three returns the SAME instance the
 * Zodiac launcher built, so this hook and the switcher share one client, one
 * refresh token and one in-flight silent check. Get one character wrong — a
 * trailing slash, a different issuer fallback — and you silently build a
 * SECOND client that races the first for the same refresh token.
 *
 * These three values are duplicated from AuthCallback.jsx:16-20. They must
 * stay identical to it, including the localhost fallback.
 */
import { useCallback, useEffect, useState } from "react";
// @ts-ignore — zodiacAuth.js is plain JS, synced from zodiac-embed, no types
import { createAuth, notifySignedOut } from "../ZodiacLauncher/zodiacAuth";

export type SessionStatus = "unknown" | "authenticated" | "anonymous";

export interface ZodiacUser {
  name?: string;
  preferred_username?: string;
  email?: string;
  [claim: string]: unknown;
}

/** Must match AuthCallback.jsx:16-20 exactly — see the note above. */
export function getAuthClient() {
  return createAuth({
    issuer: process.env.REACT_APP_IDP_ISSUER || "http://localhost:5062",
    clientId: "indexx-web",
    redirectUri: window.location.origin + "/auth/callback",
  });
}

/** The first name, for the greeting. Falls back through what the claims offer. */
export function firstNameOf(user: ZodiacUser | null): string | null {
  if (!user) return null;
  const full =
    (typeof user.name === "string" && user.name) ||
    (typeof user.preferred_username === "string" && user.preferred_username) ||
    "";
  const first = full.trim().split(/\s+/)[0];
  if (first) return first;
  // No name claim at all. The local part of an email is a poor greeting but a
  // better one than "Welcome back, undefined".
  const email = typeof user.email === "string" ? user.email : "";
  return email.split("@")[0] || null;
}

export function useZodiacSession() {
  const [status, setStatus] = useState<SessionStatus>("unknown");
  const [user, setUser] = useState<ZodiacUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    let auth: any;
    try {
      auth = getAuthClient();
    } catch (_) {
      // createAuth throws without an issuer/clientId/redirectUri. A broken
      // config must leave the page readable, signed out.
      setStatus("anonymous");
      return;
    }

    const settle = () => {
      if (cancelled) return;
      try {
        setStatus(auth.getStatus());
        setUser(auth.getUser());
      } catch (_) {
        setStatus("anonymous");
      }
    };

    /*
     * And again every time a session lands after this effect has answered.
     *
     * settle() reads what the client knows NOW, so one function serves the
     * first answer and every later one. zodiacAuth dispatches "zodiac:session"
     * from applyEstateSession once the estate keys are written — on a cold
     * visit that is a round trip AFTER first paint, and on a silent re-auth it
     * is later still. Without this the page keeps the pre-session answer and
     * nothing tells it to look again; the full reload that used to rescue it
     * is exactly what LandingHeader now opts out of.
     *
     * Registered before the two branches below so it is in place whichever of
     * them answers first, and removed on unmount.
     */
    window.addEventListener("zodiac:session", settle);

    /*
     * Already answered in this tab — no round trip, no flash.
     *
     * This is the common case on every navigation after the first, and it is
     * why the check is worth making before calling silentSignIn at all.
     */
    if (auth.hasAttemptedSilentSignIn()) {
      settle();
    } else {
      /*
       * Safe to call even though the Zodiac launcher in the header calls it
       * too: silentSignIn is latched per page load, so whichever runs second
       * resolves immediately with `already_attempted` rather than firing a
       * second redirect. Resolving — never rejecting — is part of its
       * contract; it runs on page load in a dozen products and must never take
       * a render down.
       */
      auth.silentSignIn().then(settle).catch(settle);
    }

    return () => {
      cancelled = true;
      window.removeEventListener("zodiac:session", settle);
    };
  }, []);

  /*
   * Sign out of the ECOSYSTEM, not just this tab.
   *
   * Clearing localStorage — which is all most products' logout ever did —
   * empties the browser and tells the provider nothing: the IdP session row
   * survives, the next page load's silent check finds it, and the person is
   * signed straight back in. On a shared machine the next person inherits it.
   * Ending it at the provider revokes the refresh family and kills the
   * first-party cookie, which is what makes the design's "Sign out of
   * everything" literally true.
   *
   * ── Why notifySignedOut and not getAuthClient().signOut ──────────────────
   *
   * Both reach the same client today, but only because the memo key above
   * still matches the launcher's to the character. That is exactly the
   * fragility the module function exists to remove (zodiacAuth.js:325-331):
   * rebuild the client with one character different and you get a SECOND one
   * that holds no tokens, revokes nothing, and cheerfully reports success.
   * notifySignedOut names nothing — it asks whichever launcher is already on
   * the page, so it cannot drift.
   *
   * It NAVIGATES, so it goes last and may not come back. It never throws, and
   * resolves false when no launcher client has been built yet; only then is
   * there a local route left to take.
   */
  const signOut = useCallback(async () => {
    const ended = await notifySignedOut({
      returnTo: window.location.origin + "/",
    }).catch(() => false);
    // Never trap someone in a session they asked to leave.
    if (!ended) window.location.href = "/";
  }, []);

  return { status, user, firstName: firstNameOf(user), signOut };
}
